import React, { useEffect, useState } from 'react';
import { getDoctorByEmail, getPatientsByDoctor } from '../api/database';
import TimelineTab from './history/TimelineTab';

type Page =
  | 'login'
  | 'register'
  | 'dashboard'
  | 'history'
  | 'timeline'
  | 'settings'
  | 'consent'
  | 'patients'
  | 'thresholds'
  | 'medication'
  | 'verify'
  | 'verify-patient'
  | 'reset-password-patient'
  | 'forgot-password'
  | 'reset-password';

type TimelinePageProps = {
  onLogout: () => void;
  onNavigate: (page: Page) => void;
  userRole?: string;
  isDemo?: boolean;
  patId?: string;
  onPatIdChange?: (patId: string) => void;
};

const TimelinePage: React.FC<TimelinePageProps> = ({ onLogout, onNavigate, userRole, isDemo = false, patId, onPatIdChange }) => {
  const isDoctorTimeline = Boolean(userRole === 'doctor' && !isDemo);
  const effectivePatId = patId || '';

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(() => !effectivePatId);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [patients, setPatients] = useState<Array<{ pat_id: string; first_name: string; last_name: string; dev_id: string }>>([]);
  const [selectedPatientName, setSelectedPatientName] = useState('');

  useEffect(() => {
    if (!isDoctorTimeline) return;
    const loadPatients = async () => {
      setPatientsLoading(true);
      try {
        const email = localStorage.getItem('currentUserEmail') || '';
        if (!email) return;
        const doc = await getDoctorByEmail(email);
        if (!doc?.doc_id) return;
        const pats = await getPatientsByDoctor(doc.doc_id);
        setPatients(pats);
      } catch (error) {
        console.error('Error loading patients for timeline:', error);
      } finally {
        setPatientsLoading(false);
      }
    };
    loadPatients();
  }, [isDoctorTimeline]);

  useEffect(() => {
    if (!isDoctorTimeline || !effectivePatId) {
      setSelectedPatientName('');
      return;
    }
    const p = patients.find((x) => String(x.pat_id) === String(effectivePatId));
    setSelectedPatientName(p ? `${p.first_name} ${p.last_name}`.trim() || `Patient ${effectivePatId}` : `Patient ${effectivePatId}`);
  }, [patients, effectivePatId, isDoctorTimeline]);

  useEffect(() => {
    if (!isDoctorTimeline) return;
    setIsPatientModalOpen(!effectivePatId);
  }, [isDoctorTimeline, effectivePatId]);

  const selectPatient = (id: string) => {
    setIsPatientModalOpen(false);
    onPatIdChange?.(id);
  };

  const patientListModal = isDoctorTimeline ? (
    <div
      className="modal-backdrop"
      onClick={() => setIsPatientModalOpen(false)}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'hidden',
          animation: 'modalSlideIn 0.3s ease-out',
        }}
      >
        <div
          className="modal-header"
          style={{
            padding: '24px 24px 16px 24px',
            borderBottom: '1px solid #f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '1.8rem' }}>🕒</span>
              Select Patient
            </h3>
            <p style={{ margin: '8px 0 0 0', color: '#7f8c8d', fontSize: '0.95rem', fontWeight: '400' }}>
              Choose which patient's timeline you want to view
            </p>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={() => setIsPatientModalOpen(false)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.8rem',
              color: '#bdc3c7',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
              e.currentTarget.style.color = '#e74c3c';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#bdc3c7';
            }}
          >
            ×
          </button>
        </div>
        <div className="modal-body" style={{ padding: '20px 24px', maxHeight: '400px', overflowY: 'auto' }}>
          {patientsLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>Loading patients...</div>
          ) : patients.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>No patients found.</div>
          ) : (
            <div className="patient-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {patients.map((patient) => (
                <div
                  key={patient.pat_id}
                  className="patient-item"
                  onClick={() => selectPatient(patient.pat_id)}
                  style={{
                    padding: '20px',
                    border: '2px solid #ecf0f1',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    backgroundColor: '#fff',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#3498db';
                    e.currentTarget.style.backgroundColor = '#f8f9ff';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(52, 152, 219, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#ecf0f1';
                    e.currentTarget.style.backgroundColor = '#fff';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        flexShrink: 0,
                      }}
                    >
                      {(patient.first_name?.charAt(0) || '?').toUpperCase()}
                      {(patient.last_name?.charAt(0) || '?').toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', fontSize: '1.1rem', color: '#2c3e50', marginBottom: '4px' }}>
                        {[patient.first_name, patient.last_name].filter(Boolean).join(' ') || `Patient ${patient.pat_id}`}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#7f8c8d', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ backgroundColor: '#ecf0f1', padding: '2px 8px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: '500' }}>
                          ID: {patient.pat_id}
                        </span>
                      </div>
                    </div>
                    <div style={{ color: '#bdc3c7', fontSize: '1.2rem', transition: 'all 0.2s ease' }}>→</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  ) : null;

  const timelineCss = `
    .ph-root * { box-sizing: border-box; }
    .ph-root {
      margin: 0;
      font-family: Arial, Helvetica, sans-serif;
      background: #f5f5f5;
      color: #1f2937;
      min-height: calc(100vh - 72px);
    }
    .ph-page {
      max-width: 1400px;
      margin: 0 auto;
      padding: 20px;
    }
    .ph-card {
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-radius: 18px;
      box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
    }
    .ph-content-card { padding: 18px; }
    .ph-section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      margin-bottom: 6px;
      padding-bottom: 5px;
      border-bottom: 1px solid #eee;
    }
    .ph-muted { color: #64748b; }
    .ph-small { font-size: 13px; }
    .ph-btn {
      border: 1px solid #b7e3e0;
      border-radius: 12px;
      padding: 11px 12px;
      background: #ffffff;
      cursor: pointer;
      font-weight: 700;
      color: #0f4f4b;
      transition: all 0.2s ease;
    }
    .ph-btn.primary {
      background: #20B2AA;
      color: #ffffff;
      border-color: #20B2AA;
    }
    .ph-btn.quick-active {
      background: #111111;
      color: #ffffff;
      border-color: #111111;
    }
    .ph-input {
      width: 100%;
      border: 1px solid #b7e3e0;
      border-radius: 12px;
      padding: 11px 12px;
      background: #ffffff;
      font: inherit;
    }
    .ph-badge {
      display: inline-flex;
      align-items: center;
      padding: 6px 10px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 700;
      background: #e8f7f6;
      color: #0f4f4b;
      white-space: nowrap;
    }
    .ph-badge.danger { background: #fee2e2; color: #991b1b; }
    .ph-badge.warning { background: #fef3c7; color: #92400e; }
    .ph-list { display: grid; gap: 12px; }
    .ph-list-item {
      border: 1px solid #e5e7eb;
      border-radius: 16px;
      padding: 16px;
      background: #ffffff;
    }
    .ph-timeline-toolbar {
      display: grid;
      grid-template-columns: minmax(180px, 2fr) auto auto auto auto auto auto auto;
      gap: 8px;
      align-items: center;
    }
    @media (max-width: 1000px) {
      .ph-timeline-toolbar {
        grid-template-columns: 1fr;
      }
    }
    @keyframes modalSlideIn {
      from {
        opacity: 0;
        transform: scale(0.95) translateY(-12px);
      }
      to {
        opacity: 1;
        transform: scale(1) translateY(0);
      }
    }
  `;

  return (
    <div className="dashboard">
      <header className="header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="hamburger-btn" onClick={() => setIsSidebarOpen(true)} aria-label="Open menu">☰</button>
          <h1>Timeline</h1>
        </div>
        <div />
      </header>

      <div className={`sidebar ${isSidebarOpen ? 'open' : ''}`} role="navigation" aria-label="Main menu">
        <ul className="sidebar-menu">
          <li onClick={() => { setIsSidebarOpen(false); if (userRole === 'doctor' && !isDemo) onNavigate('patients'); else onNavigate('dashboard'); }}>
            <span className="mi">🏠</span><span>Overview</span>
          </li>
          <li onClick={() => { setIsSidebarOpen(false); onNavigate('history'); }}><span className="mi">🕒</span><span>History</span></li>
          <li onClick={() => setIsSidebarOpen(false)}><span className="mi">📈</span><span>Timeline</span></li>
          <li onClick={() => { setIsSidebarOpen(false); onNavigate('settings'); }}><span className="mi">⚙️</span><span>Settings</span></li>
          <li onClick={() => { setIsSidebarOpen(false); onNavigate('consent'); }}><span className="mi">📝</span><span>Consent</span></li>
          <li onClick={() => { setIsSidebarOpen(false); onLogout(); }}><span className="mi">↪</span><span>Logout</span></li>
        </ul>
        <div className="sidebar-footer"><div className="sidebar-brand"></div></div>
      </div>
      <div className={`sidebar-backdrop ${isSidebarOpen ? 'show' : ''}`} onClick={() => setIsSidebarOpen(false)} />

      <div className="ph-root">
        <style>{timelineCss}</style>
        <main className="ph-page">
          <TimelineTab
            patId={effectivePatId}
            isDemo={isDemo}
            selectedPatientName={selectedPatientName}
            onSelectPatient={() => setIsPatientModalOpen(true)}
          />
        </main>
      </div>
      {isDoctorTimeline && isPatientModalOpen && patientListModal}
    </div>
  );
};

export default TimelinePage;
